<template>
    <div v-if="loading" class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>
    <div v-else-if="error" class="text-center text-danger py-3 small">
        <i class="fas fa-exclamation-triangle me-1"></i> {{ error }}
    </div>
    <div v-else class="token-stats-grid mt-2 mb-3">
         <div>
            <small class="text-muted d-block">Price</small>
            <span class="fw-bold">${{ formatPrice(tokenData?.price_usd) }}</span>
             <span :class="changeClass" class="ms-1 small">{{ formatPriceChange(tokenData?.change_24h) }}</span>
        </div>
        <div>
            <small class="text-muted d-block">Volume (24h)</small>
            <span class="fw-bold">${{ formatNumber(tokenData?.volume_24h) }}</span>
        </div>
        <div>
            <small class="text-muted d-block">Liquidity</small>
            <span class="fw-bold">${{ formatNumber(tokenData?.liquidity_usd) }}</span>
        </div>
         <div>
            <small class="text-muted d-block">Trades (24h)</small>
            <span class="fw-bold">{{ formatNumber(trades24h) }}</span>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';

const props = defineProps({
    asset: {
        type: Object,
        required: true,
    }
});

const tokenData = ref(null);
const tokenStats = ref(null); // For trade counts etc.
const loading = ref(false);
const error = ref(null);

// Computed property for 24h trades remains the same
const trades24h = computed(() => {
    return tokenStats.value?.['24h']?.total?.trades || 0;
});

// --- Formatting Helpers (Keep these) ---
const formatPrice = (price) => {
    if (price === null || price === undefined) return '--';
    if (price === 0) return '0.00';
    if (price > 1) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price > 0.01) return price.toFixed(4);
    const priceStr = price.toString();
    const decimalPart = priceStr.split('.')[1];
    if (!decimalPart) return price.toFixed(2);
    let firstSignificantDigitIndex = decimalPart.search(/[^0]/);
    if (firstSignificantDigitIndex === -1) return '0.00';
    const totalDecimalPlaces = firstSignificantDigitIndex + 3; // Show 3 significant digits
    return price.toFixed(totalDecimalPlaces);
};

const formatNumber = (num) => {
    if (num === null || num === undefined) return '--';
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(0) + 'K'; // No decimals for K
    return num?.toLocaleString() ?? '--';
};

const formatPriceChange = (change) => {
    if (change === null || change === undefined) return ''; // Return empty string if no change data
    const sign = change >= 0 ? '+' : '';
    return `(${sign}${change.toFixed(2)}%)`;
};

const changeClass = computed(() => {
    const change = tokenData.value?.change_24h;
    if (change === null || change === undefined) return 'text-muted';
    return change >= 0 ? 'text-success' : 'text-danger';
});
// --- End Formatting Helpers ---

// Fetching logic remains the same
const fetchData = async () => {
    if (!props.asset.address) {
        error.value = 'Asset address missing.';
        return;
    }
    loading.value = true;
    error.value = null;
    // Also control the static indicators in the parent card
    const parentCard = document.getElementById(`asset-card-${props.asset.address}`)
    const loadingIndicator = parentCard?.querySelector('.asset-loading-indicator');
    const errorIndicator = parentCard?.querySelector('.asset-error-indicator');
    if (loadingIndicator) loadingIndicator.style.display = 'inline-block';
    if (errorIndicator) errorIndicator.style.display = 'none';

    try {
        const [priceRes, statsRes] = await Promise.all([
            axios.get(`/api/tokens/${props.asset.address}/price`),
            axios.get(`/api/tokens/${props.asset.address}/stats`)
        ]);
        tokenData.value = priceRes.data;
        tokenStats.value = statsRes.data;

    } catch (err) {
        console.error(`Error fetching data for asset ${props.asset.address}:`, err);
        error.value = 'Data Error'; // Keep error message short
        tokenData.value = null;
        tokenStats.value = null;
        if (errorIndicator) errorIndicator.style.display = 'inline-block';
    } finally {
        loading.value = false;
        if (loadingIndicator) loadingIndicator.style.display = 'none';
    }
};

onMounted(() => {
    fetchData();
    // Optional polling can be added here if needed
});

</script>

<style scoped>
/* Scoped styles from previous version */
.token-stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); /* Responsive grid */
    gap: 0.75rem; /* Space between items */
    font-size: 0.9em;
    border-top: 1px solid var(--bs-border-color-translucent);
    border-bottom: 1px solid var(--bs-border-color-translucent);
    padding: 0.75rem 0;
}
.token-stats-grid > div {
    text-align: left;
}
.text-success { color: #198754 !important; }
.text-danger { color: #dc3545 !important; }
.text-muted { color: #6c757d !important; } /* Ensure muted text color */
</style>
